import { useState } from "react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { PackagePlus, History, Barcode, Camera, Printer, Search, FileSpreadsheet } from "lucide-react";
import { api, inputCls, labelCls, rd, ADJUST_REASONS, askPin, beep, CATEGORIES, printProductCatalog } from "./posShared";
import CameraScanner from "./CameraScanner";

export default function Inventario({ products, movements, settings, reload }) {
  const [sel, setSel] = useState("");
  const [reason, setReason] = useState("compra");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [scan, setScan] = useState("");
  const [camera, setCamera] = useState(false);
  const [listQ, setListQ] = useState("");

  const catLabel = (k) => (CATEGORIES.find((c) => c.key === k) || {}).label || k || "";
  const listed = products.filter((p) => {
    const t = listQ.toLowerCase();
    return !t || p.name.toLowerCase().includes(t) || (p.code || "").toLowerCase().includes(t) || (p.barcode || "").toLowerCase().includes(t);
  });

  const exportXlsx = () => {
    if (!products.length) return toast.error("No hay productos para exportar");
    const rows = products.map((p) => ({
      Producto: p.name,
      Código: p.code || "",
      "Código de barras": p.barcode || "",
      Descripción: p.description || "",
      Categoría: catLabel(p.category),
      "Precio de compra": Number(p.cost) || 0,
      "Precio de venta": Number(p.price) || 0,
      Stock: Number(p.qty) || 0,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = [{ wch: 28 }, { wch: 12 }, { wch: 16 }, { wch: 32 }, { wch: 14 }, { wch: 16 }, { wch: 16 }, { wch: 8 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Productos");
    XLSX.writeFile(wb, `productos-mobyvir-${new Date().toISOString().slice(0, 10)}.xlsx`);
    toast.success("Lista exportada a Excel");
  };

  const reasonObj = ADJUST_REASONS.find((r) => r.key === reason);

  const handleCode = (raw) => {
    const code = String(raw || "").trim().toLowerCase();
    setScan("");
    if (!code) return;
    const m = products.find(
      (p) => (p.barcode || "").toLowerCase() === code || (p.code || "").toLowerCase() === code
    );
    if (m) { setSel(m.id); beep(true); toast.success(`Producto: ${m.name} (stock: ${m.qty})`); }
    else { beep(false); toast.error("Producto no encontrado"); }
  };
  const onScan = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    handleCode(scan);
  };

  const submit = async () => {
    if (!sel) return toast.error("Selecciona un producto");
    let sign = reasonObj.sign;
    if (sign === 0) sign = 1; // correccion: user sets direction via +/- qty
    let change = Math.abs(Number(qty)) * (reason === "correccion" ? Math.sign(Number(qty)) || 1 : sign);
    const body = { qty_change: change, reason, note, pin: "" };
    if (change < 0) body.pin = await askPin();
    try {
      await api.post(`/pos/products/${sel}/adjust`, body);
      toast.success("Inventario actualizado");
      setQty(1); setNote("");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div>
      <h1 className="font-serif text-3xl mb-6">Inventario</h1>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="flex items-center gap-2 font-medium mb-4"><PackagePlus size={18} className="text-rose" /> Ajuste de inventario</h2>
          <div className="space-y-3">
            <div><label className={labelCls}>Escanear código de barras</label>
              <div className="relative">
                <input data-testid="pos-inv-scan" value={scan} onChange={(e) => setScan(e.target.value)} onKeyDown={onScan} placeholder="Escanea o escribe el código y presiona Enter..." className={`${inputCls} pr-16`} />
                <button data-testid="pos-inv-camera" onClick={() => setCamera(true)} title="Escanear con la cámara" className="absolute right-9 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-rose"><Camera size={16} /></button>
                <Barcode size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-rose" />
              </div>
            </div>
            <div><label className={labelCls}>Producto</label>
              <select data-testid="pos-inv-product" value={sel} onChange={(e) => setSel(e.target.value)} className={inputCls}>
                <option value="">Selecciona...</option>
                {products.map((p) => <option key={p.id} value={p.id}>{p.name} (stock: {p.qty})</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Motivo</label>
              <select data-testid="pos-inv-reason" value={reason} onChange={(e) => setReason(e.target.value)} className={inputCls}>
                {ADJUST_REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
              </select>
            </div>
            <div><label className={labelCls}>Cantidad {reason === "correccion" ? "(usa − para restar)" : reasonObj.sign > 0 ? "(se suma)" : "(se resta)"}</label>
              <input data-testid="pos-inv-qty" type="number" value={qty} onChange={(e) => setQty(e.target.value)} className={inputCls} />
            </div>
            <div><label className={labelCls}>Nota</label><input value={note} onChange={(e) => setNote(e.target.value)} className={inputCls} /></div>
            <p className="text-xs text-neutral-400">Las reducciones requieren PIN de administrador.</p>
            <button data-testid="pos-inv-submit" onClick={submit} className="w-full bg-espresso text-cream rounded-full py-3 text-xs uppercase tracking-[0.15em] hover:bg-rose transition">Aplicar ajuste</button>
          </div>
        </div>

        <div className="bg-white border border-neutral-200 rounded-xl p-5">
          <h2 className="flex items-center gap-2 font-medium mb-4"><History size={18} className="text-rose" /> Movimientos recientes</h2>
          <div className="space-y-2 max-h-[420px] overflow-y-auto">
            {movements.map((m) => (
              <div key={m.id} className="flex items-center justify-between text-sm border-b border-neutral-50 py-2">
                <div>
                  <div className="font-medium text-espresso">{m.product_name}</div>
                  <div className="text-xs text-neutral-400 capitalize">{m.reason.replace("_", " ")} · {(m.created_at || "").slice(0, 16).replace("T", " ")}</div>
                </div>
                <span className={`font-medium ${m.qty_change < 0 ? "text-red-500" : "text-emerald-600"}`}>{m.qty_change > 0 ? "+" : ""}{m.qty_change}</span>
              </div>
            ))}
            {movements.length === 0 && <p className="text-neutral-400 text-sm py-6 text-center">Sin movimientos.</p>}
          </div>
        </div>
      </div>

      <div className="mt-6 bg-white border border-neutral-200 rounded-xl p-5">
        <h2 className="font-medium mb-4">Valor del inventario</h2>
        <p className="text-3xl font-serif">{rd(products.reduce((s, p) => s + (p.cost || 0) * (p.qty || 0), 0))}</p>
        <p className="text-xs text-neutral-400 mt-1">Suma de (costo × cantidad) de todos los productos.</p>
      </div>

      <div className="mt-6 bg-white border border-neutral-200 rounded-xl p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="flex items-center gap-2 font-medium"><Barcode size={18} className="text-rose" /> Lista de productos <span className="text-xs text-neutral-400 font-normal">({products.length})</span></h2>
          <div className="flex flex-wrap gap-2">
            <button data-testid="pos-inv-export-xlsx" onClick={exportXlsx}
              className="inline-flex items-center gap-2 border border-espresso text-espresso rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-espresso hover:text-cream transition">
              <FileSpreadsheet size={15} /> Exportar a Excel
            </button>
            <button data-testid="pos-inv-print-catalog" onClick={() => printProductCatalog(products, settings || {})}
              className="inline-flex items-center gap-2 bg-espresso text-cream rounded-full px-5 py-2.5 text-xs uppercase tracking-[0.12em] hover:bg-rose transition">
              <Printer size={15} /> Imprimir / Descargar PDF
            </button>
          </div>
        </div>
        <div className="relative mb-4 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <input data-testid="pos-inv-list-search" value={listQ} onChange={(e) => setListQ(e.target.value)}
            placeholder="Buscar por nombre, código o código de barras..." className={`${inputCls} pl-9`} />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="pos-inv-list-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-100">
                <th className="px-3 py-2.5">Producto</th>
                <th className="px-3 py-2.5">Descripción</th>
                <th className="px-3 py-2.5">Categoría</th>
                <th className="px-3 py-2.5 text-right">P. compra</th>
                <th className="px-3 py-2.5 text-right">P. venta</th>
                <th className="px-3 py-2.5 text-center">Stock</th>
              </tr>
            </thead>
            <tbody>
              {listed.map((p) => (
                <tr key={p.id} data-testid={`pos-inv-list-row-${p.id}`} className="border-b border-neutral-50 hover:bg-neutral-50/60">
                  <td className="px-3 py-2.5">
                    <div className="font-medium text-espresso">{p.name}</div>
                    {p.code && <div className="text-xs text-neutral-400">{p.code}</div>}
                  </td>
                  <td className="px-3 py-2.5 text-neutral-500 max-w-xs">{p.description || "—"}</td>
                  <td className="px-3 py-2.5 text-neutral-600">{catLabel(p.category)}</td>
                  <td className="px-3 py-2.5 text-right text-neutral-600">{rd(p.cost)}</td>
                  <td className="px-3 py-2.5 text-right font-medium">{rd(p.price)}</td>
                  <td className="px-3 py-2.5 text-center">{p.qty ?? 0}</td>
                </tr>
              ))}
              {listed.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-neutral-400">Sin productos.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {camera && <CameraScanner onDetect={handleCode} onClose={() => setCamera(false)} />}
    </div>
  );
}
